/**
 * One canonical patient shape for the global patient context.
 *
 * The API and the existing screens disagree on key names (`patient_id` vs
 * `pat_id`, `name` vs `patient_name`). Everything that enters the context is
 * normalized here; everything that leaves for a legacy screen goes through
 * `toLegacyPatient` so those screens keep working untouched.
 */

export const normalizePatient = (raw) => {
  if (!raw) return null;
  return {
    patient_id: raw.patient_id ?? raw.pat_id ?? null,
    name: raw.name ?? raw.patient_name ?? "",
    first_name: raw.first_name ?? "",
    middle_name: raw.middle_name ?? "",
    last_name: raw.last_name ?? "",
    gender: raw.gender ?? "",
    dob: raw.dob ?? "",
    age: raw.age ?? "",
    phone_no: raw.phone_no ?? "",
    alternate_phone_no: raw.alternate_phone_no ?? "",
    email: raw.email ?? raw.patient_email ?? "",
    abha_id: raw.abha_id ?? "",
    abha_no: raw.abha_no ?? "",
    photo: raw.photo ?? "",
  };
};

/**
 * Adapter for screens that still read the older duplicated key names
 * (billing wants `pat_id` / `patient_name` / `patient_email`).
 */
export const toLegacyPatient = (patient) => {
  if (!patient) return null;
  return {
    ...patient,
    pat_id: patient.patient_id,
    patient_name: patient.name,
    patient_email: patient.email,
  };
};

/** Age in whole years from `dob`, falling back to an `age` field if present. */
export const patientAgeYears = (patient) => {
  if (!patient) return "";
  if (patient.dob) {
    const dob = new Date(patient.dob);
    if (!Number.isNaN(dob.getTime())) {
      const now = new Date();
      let years = now.getFullYear() - dob.getFullYear();
      const monthDiff = now.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
        years -= 1;
      }
      if (years >= 0 && years < 150) return years;
    }
  }
  const age = parseInt(patient.age, 10);
  return Number.isNaN(age) ? "" : age;
};
